/**
 * Hook pour gérer les intégrations (Twitch, Spotify, Mail)
 * @module admin/hooks/useIntegrations
 */

import { useState, useCallback } from 'react';
import {
  getIntegrationStatus,
  testIntegration,
  testMail,
} from '../services/maintenanceService';

/**
 * Hook de gestion des intégrations
 * @returns {Object} État et fonctions pour les intégrations
 */
export const useIntegrations = () => {
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceResult, setMaintenanceResult] = useState(null);
  const [maintenanceError, setMaintenanceError] = useState(null);

  /**
   * Récupère le statut des intégrations
   */
  const fetchIntegrationStatus = useCallback(async () => {
    try {
      const data = await getIntegrationStatus();
      setIntegrationStatus(data.data);
    } catch (err) {
      console.error(
        'Erreur lors de la récupération du statut des intégrations:',
        err
      );
    }
  }, []);

  /**
   * Teste une intégration
   * @param {string} service - 'twitch' ou 'spotify'
   */
  const handleTestIntegration = useCallback(async (service) => {
    setMaintenanceLoading(true);
    setMaintenanceResult(null);
    setMaintenanceError(null);

    try {
      const data = await testIntegration(service);
      setMaintenanceResult(data);
      await fetchIntegrationStatus();
    } catch (err) {
      setMaintenanceError(err.message);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [fetchIntegrationStatus]);

  /**
   * Teste la connexion mail et envoie un email de test
   * @param {string} [email] - Adresse de destination (optionnel)
   */
  const handleTestMail = useCallback(async (email) => {
    setMaintenanceLoading(true);
    setMaintenanceResult(null);
    setMaintenanceError(null);

    try {
      const data = await testMail(email);
      setMaintenanceResult(data);
      await fetchIntegrationStatus();
    } catch (err) {
      setMaintenanceError(err.message);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [fetchIntegrationStatus]);

  return {
    integrationStatus,
    maintenanceLoading,
    maintenanceResult,
    maintenanceError,
    fetchIntegrationStatus,
    handleTestIntegration,
    handleTestMail,
  };
};
